/**
 * Sidebar Navigation Component - Pro Terminal Version
 * 
 * SESSION 57: SSoT REFACTOR - SINGLE SOURCE OF TRUTH
 * 
 * ARCHITECTURE:
 * - Uses MarketProvider context as THE ONLY data source
 * - NO duplicate REST API polling - MarketProvider handles all polling
 * - God Score, prices, and metadata all from context store
 * 
 * Navigation to Dashboard, Analytics, Admin
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  BarChart3, 
  Settings, 
  ChevronLeft, 
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Minus,
  LogOut,
  // Clock removed - SESSION 92: Replay link removed (duplicate of Time Machine)
  History,  // SESSION 91: Time Machine icon
  Timer,    // SESSION 109: Subscription countdown icon
  HelpCircle  // SESSION 122: FAQ icon
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useMarket } from '../contexts/MarketProvider';
import FAQModal from './FAQModal';  // SESSION 122: FAQ Modal

// ============================================
// SESSION 109: Subscription Countdown Component
// ============================================
// Shows "X days remaining" for non-admin users
// Auto-triggers logout when subscription expires
// ============================================
const SubscriptionCountdown = ({ endDate, isCollapsed, onExpired }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, expired: false });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const end = new Date(endDate);
      const now = new Date();
      const diff = end - now;

      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, expired: true });
        // Trigger logout after a short delay to show the expired message
        setTimeout(() => onExpired?.('SUBSCRIPTION_EXPIRED'), 2000);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      setTimeLeft({ days, hours, minutes, expired: false });
    };

    calculateTimeLeft();
    // Update every minute
    const interval = setInterval(calculateTimeLeft, 60000);
    return () => clearInterval(interval);
  }, [endDate, onExpired]);

  // Determine urgency color
  const getUrgencyColor = () => {
    if (timeLeft.expired) return 'text-red-400 bg-red-500/10 border-red-500/30';
    if (timeLeft.days <= 3) return 'text-amber-400 bg-amber-500/10 border-amber-500/30';
    if (timeLeft.days <= 7) return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30';
    return 'text-cyan-400 bg-cyan-500/10 border-cyan-500/30';
  };

  // Format display text
  const getDisplayText = () => {
    if (timeLeft.expired) return 'Expired';
    if (timeLeft.days > 0) return `${timeLeft.days}d ${timeLeft.hours}h`;
    if (timeLeft.hours > 0) return `${timeLeft.hours}h ${timeLeft.minutes}m`;
    return `${timeLeft.minutes}m`;
  };

  return (
    <div 
      className={`mx-2 mb-2 rounded-lg border ${getUrgencyColor()} p-2 transition-all`}
      data-testid="subscription-countdown"
    >
      {isCollapsed ? (
        <div className="flex flex-col items-center">
          <Timer className="w-4 h-4 mb-1" />
          <span className="text-[10px] font-bold">{timeLeft.days}d</span>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Timer className="w-4 h-4 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Subscription</p>
            <p className="text-sm font-semibold">{getDisplayText()}</p>
          </div>
          {timeLeft.days <= 7 && !timeLeft.expired && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-current/10">
              {timeLeft.days <= 3 ? 'Expiring Soon!' : 'Low'}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export const Sidebar = ({ defaultCollapsed = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin, user, logout } = useAuth();
  
  // ====================================================================
  // SESSION 57: SSoT - SINGLE SOURCE OF TRUTH FROM MARKET CONTEXT
  // ====================================================================
  // All data comes from MarketProvider context - NO independent polling
  // ====================================================================
  const marketContext = useMarket();
  const activeSymbol = marketContext?.activeSymbol || 'NIFTY';
  const setActiveSymbol = marketContext?.setActiveSymbol || (() => {});
  
  // Store contains all data: prices, godScore, gexLevels, etc.
  const store = marketContext?.store || {};
  const connectionStatus = marketContext?.connectionStatus || {};
  const isConnected = connectionStatus?.status === 'live' || connectionStatus?.status === 'replay';
  
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  
  // SESSION 86o: Track market interlock status for Replay link
  const [marketInterlock, setMarketInterlock] = useState(null);
  
  // Track previous prices for color flash effect
  const [priceColors, setPriceColors] = useState({ 
    NIFTY: 'neutral', 
    BANKNIFTY: 'neutral' 
  });
  const prevFutPricesRef = useRef({ NIFTY: null, BANKNIFTY: null });
  const colorTimeoutRef = useRef({ NIFTY: null, BANKNIFTY: null });
  
  // SESSION 99e: Last Known Good (LKG) price to prevent flickering to garbage values
  const lastKnownGoodPriceRef = useRef({ NIFTY: null, BANKNIFTY: null });
  
  // SESSION 128: Last Known Good (LKG) VIX to prevent "--" flickering
  const lastKnownGoodVixRef = useRef(15.0);
  
  // SESSION 122: FAQ Modal state
  const [showFAQ, setShowFAQ] = useState(false);
  
  // Current time for latency calculation (updates every 500ms)
  const [currentTime, setCurrentTime] = useState(Date.now());
  
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(Date.now()), 500);
    return () => clearInterval(timer);
  }, []);
  
  // SESSION 86o: Fetch market interlock status for Replay link visibility
  useEffect(() => {
    const fetchInterlock = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const API = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API}/api/system/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setMarketInterlock(data.live_market_interlock);
        }
      } catch (e) {
        console.debug('Interlock fetch failed:', e);
      }
    };
    
    fetchInterlock();
    const interval = setInterval(fetchInterlock, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);
  
  // ====================================================================
  // SESSION 57: SSoT - DERIVE DATA FROM MARKET CONTEXT ONLY
  // ====================================================================
  // NO REST API polling - MarketProvider handles all data fetching
  // ====================================================================
  const getSymbolData = (symbol) => {
    const data = store[symbol] || {};
    const gexLevels = data.gexLevels || {};
    const latestCandle = data.latestCandle || {};
    
    // Get price from multiple sources (priority order)
    // SESSION 99e: Prioritize real-time futures price over candle close
    let futPrice = data.futuresPrice || gexLevels.futures_price || data.ltp || null;
    
    // SESSION 99e: Last Known Good (LKG) price protection
    // Prevent flickering to garbage/stale values
    // Valid NIFTY price: 15000-35000, Valid BANKNIFTY price: 30000-80000
    const minPrice = symbol === 'BANKNIFTY' ? 30000 : 15000;
    const maxPrice = symbol === 'BANKNIFTY' ? 80000 : 35000;
    const isValidPrice = futPrice && futPrice >= minPrice && futPrice <= maxPrice;
    
    if (isValidPrice) {
      // Update LKG if price is valid
      lastKnownGoodPriceRef.current[symbol] = futPrice;
    } else if (lastKnownGoodPriceRef.current[symbol]) {
      // Use LKG if current price is invalid/garbage
      futPrice = lastKnownGoodPriceRef.current[symbol];
    }
    // If still no valid price and we have candle close in valid range, use it
    if (!futPrice && latestCandle.close >= minPrice && latestCandle.close <= maxPrice) {
      futPrice = latestCandle.close;
    }
    
    const spotPrice = data.spotPrice || gexLevels.spot_price || null;
    
    // SESSION 57: Get God Score directly from context store (SSoT)
    const godScore = data.godScore || {};
    const score = godScore?.total_score ?? 0;
    const direction = godScore?.direction ?? 'NEUTRAL';
    const signalState = godScore?.signal_state ?? 'NEUTRAL';
    
    // Determine signal based on direction and state
    const signal = direction === 'UP' && signalState === 'EXECUTE' ? 'BUY' :
                  direction === 'DOWN' && signalState === 'EXECUTE' ? 'SELL' :
                  direction === 'UP' ? 'BULL' :
                  direction === 'DOWN' ? 'BEAR' : 'NEUTRAL';
    
    // Get last tick time
    const lastTickTime = latestCandle.time ? latestCandle.time * 1000 : 
                         data.lastUpdated || 0;
    
    return {
      futPrice,
      spotPrice,
      score,
      direction,
      signal,
      running: isConnected,
      lastTickTime,
    };
  };
  
  // Update price colors when prices change
  useEffect(() => {
    ['NIFTY', 'BANKNIFTY'].forEach(symbol => {
      const data = getSymbolData(symbol);
      const futPrice = data.futPrice;
      const prevPrice = prevFutPricesRef.current[symbol];
      
      if (futPrice && futPrice > 0 && prevPrice !== null && prevPrice > 0 && futPrice !== prevPrice) {
        const newColor = futPrice > prevPrice ? 'green' : 'red';
        
        // Clear existing timeout
        if (colorTimeoutRef.current[symbol]) {
          clearTimeout(colorTimeoutRef.current[symbol]);
        }
        
        // Set color and schedule reset
        setPriceColors(prev => ({ ...prev, [symbol]: newColor }));
        colorTimeoutRef.current[symbol] = setTimeout(() => {
          setPriceColors(prev => ({ ...prev, [symbol]: 'neutral' }));
        }, 600);
      }
      
      // Update ref
      if (futPrice && futPrice > 0) {
        prevFutPricesRef.current[symbol] = futPrice;
      }
    });
    
    // Cleanup
    return () => {
      Object.values(colorTimeoutRef.current).forEach(t => t && clearTimeout(t));
    };
  }, [store]);
  
  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Persist collapsed state
  useEffect(() => {
    const savedState = localStorage.getItem('sidebar_collapsed');
    if (savedState !== null) {
      setIsCollapsed(savedState === 'true');
    }
  }, []);

  // === Market status from context ===
  const isMarketOpen = connectionStatus?.status === 'live' || connectionStatus?.status === 'replay';

  const toggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('sidebar_collapsed', String(newState));
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Get signal styling based on God Score value
  // >15 = Green, <-15 = Red, else Grey
  const getSignalStyle = (signal, direction, score) => {
    if (score > 15) {
      return { color: 'text-green-400', bg: 'bg-green-500/20', border: 'border-green-500/50', icon: TrendingUp };
    }
    if (score < -15) {
      return { color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/50', icon: TrendingDown };
    }
    return { color: 'text-slate-400', bg: 'bg-slate-500/20', border: 'border-slate-500/30', icon: Minus };
  };

  // Get score color based on value (>15 green, <-15 red, else grey)
  const getScoreColor = (score) => {
    if (score > 15) return 'text-green-400';
    if (score < -15) return 'text-red-400';
    return 'text-slate-400';
  };
  
  // Format GEX value - SESSION 68f: Use 2 decimal places for buttery feel
  const formatGex = (value) => {
    if (!value && value !== 0) return '—';
    const absValue = Math.abs(value);
    const sign = value >= 0 ? '+' : '-';
    // Always 2 decimals for smooth visual movement
    return `${sign}${absValue.toFixed(2)}Cr`;
  };

  // Get compact ticker for collapsed view
  const getCompactTicker = (symbol) => {
    if (symbol === 'BANKNIFTY') return 'BNF';
    return symbol; // NIFTY stays NIFTY
  };

  // Get health status based on latency
  const getHealthStatus = (lastTickTime) => {
    if (!lastTickTime || lastTickTime === 0) return { color: 'bg-gray-500', label: 'NO DATA', latencyMs: null };
    const latencyMs = currentTime - lastTickTime;
    if (latencyMs < 1000) return { color: 'bg-green-500', label: 'HEALTHY', latencyMs };
    if (latencyMs < 5000) return { color: 'bg-yellow-500', label: 'LAGGING', latencyMs };
    return { color: 'bg-red-500', label: 'STALE', latencyMs };
  };
  
  // Format latency for tooltip
  const formatLatency = (ms) => {
    if (ms === null) return 'No data';
    if (ms < 1000) return `${ms}ms ago`;
    return `${(ms / 1000).toFixed(1)}s ago`;
  };

  // Render Mini-Card for instrument
  const renderInstrumentCard = (symbol, path) => {
    // SESSION 56e: Get data from MarketProvider context
    const data = getSymbolData(symbol);
    const futPrice = data.futPrice;
    const spotPrice = data.spotPrice;
    const priceColor = priceColors[symbol] || 'neutral';
    
    // Use activeSymbol from context for selection state (not route-based)
    const isSelected = activeSymbol === symbol;
    const score = Math.round(data.score);
    const style = getSignalStyle(data.signal, data.direction, score);
    const health = getHealthStatus(data.lastTickTime);
    
    // Handle click to select symbol
    // SESSION 70b: INSTANT TOGGLE - Just change activeSymbol, NO navigation
    // Navigation causes full component remount which creates 1-2s lag
    const handleSelect = (e) => {
      e.preventDefault();
      setActiveSymbol(symbol);
      // NOTE: We intentionally DON'T navigate to avoid remounting the dashboard
      // The context activeSymbol change will instantly switch all displays
    };
    
    // Health Pulse Indicator Component
    // STRICT FREEZE: Disable pulse animation when market is closed
    const HealthPulse = () => (
      <div 
        className="relative group cursor-help"
        title={`Last Tick: ${formatLatency(health.latencyMs)}`}
      >
        {/* Pulse ring animation for healthy status - ONLY when market is OPEN */}
        {health.color === 'bg-green-500' && isMarketOpen && (
          <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" />
        )}
        <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${isMarketOpen ? health.color : 'bg-gray-500'}`} />
        {/* Tooltip on hover */}
        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-black/90 text-[10px] text-white rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50 pointer-events-none">
          <span className="font-bold">{isMarketOpen ? health.label : 'MARKET CLOSED'}</span>
          <br />
          {isMarketOpen ? `Last Tick: ${formatLatency(health.latencyMs)}` : 'Data Frozen'}
        </div>
      </div>
    );
    
    return (
      <div
        key={symbol}
        onClick={handleSelect}
        className={`
          block px-2 py-2 mx-1 mb-1 rounded-md transition-all cursor-pointer
          ${isSelected 
            ? 'bg-[var(--accent-primary-glow)] border-l-2 border-l-[var(--accent-primary)] ring-1 ring-[var(--accent-primary)]/30' 
            : 'hover:bg-[var(--bg-surface)] border-l-2 border-l-transparent'
          }
        `}
        data-testid={`nav-${symbol.toLowerCase()}`}
      >
        {isCollapsed ? (
          // Collapsed: Compact view with FUT price
          <div className="flex flex-col items-center gap-0.5 py-1">
            {/* Health Pulse + Compact Ticker */}
            <div className="flex items-center gap-1">
              <HealthPulse />
              <span className={`text-[10px] font-bold ${isSelected ? 'text-white' : 'text-[var(--text-primary)]'}`}>
                {getCompactTicker(symbol)}
              </span>
            </div>
            {/* Compact FUT Price with 2 decimal precision - STRICT: Use WebSocket-driven priceColor */}
            <span className={`text-[9px] font-mono font-bold ${
              priceColor === 'green' ? 'text-profit' : 
              priceColor === 'red' ? 'text-loss' : 'text-[var(--text-secondary)]'
            }`}>
              {futPrice !== null && futPrice > 0 ? futPrice.toFixed(2) : '—'}
            </span>
            {/* God Score Badge (replaces circle) */}
            <div className={`
              px-1.5 py-0.5 rounded text-[10px] font-bold
              ${style.bg} ${style.border} border ${style.color}
            `}>
              {score}
            </div>
          </div>
        ) : (
          // Expanded: Full Mini-Card with HFT + GEX breakdown
          <>
            {/* Row 1: Symbol + Health Pulse + GOD SCORE Badge */}
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <HealthPulse />
                <span className={`text-[12px] font-bold ${isSelected ? 'text-white' : 'text-[var(--text-primary)]'}`}>
                  {symbol}
                </span>
                {/* Selection indicator */}
                {isSelected && (
                  <span className="text-[8px] text-white font-bold px-1 py-0.5 bg-white/20 rounded">
                    ACTIVE
                  </span>
                )}
              </div>
              {/* GOD SCORE Badge - Replaces WAIT */}
              <div className={`
                flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-bold
                ${style.bg} ${style.border} border ${style.color}
              `}>
                {score}
              </div>
            </div>
            
            {/* Row 2: Bloomberg-Style Price Display - FUT | SPOT with 2 decimal precision */}
            {/* SESSION 56: STRICT SEPARATION - Futures and Spot are COMPLETELY INDEPENDENT */}
            <div className="flex items-center justify-between text-[10px] font-mono bg-[var(--bg-surface)]/50 rounded px-1 py-0.5 border border-[var(--border-color)]/30">
              {/* FUTURES PRICE - ONLY from TRADE_UPDATE/FUTURES_UPDATE via WebSocket */}
              <div className="flex items-center gap-0.5">
                <span className="text-cyan-400 font-semibold text-[8px]">F</span>
                <span className={`font-bold ${
                  priceColor === 'green' ? 'text-profit' : 
                  priceColor === 'red' ? 'text-loss' : 'text-[var(--text-primary)]'
                }`}>
                  {futPrice !== null && futPrice > 0 ? futPrice.toFixed(2) : '—'}
                </span>
              </div>
              <span className="text-[var(--text-muted)] text-[8px]">|</span>
              {/* SPOT PRICE - ONLY from SPOT_UPDATE/INDEX_UPDATE via WebSocket */}
              <div className="flex items-center gap-0.5" title={spotPrice !== null ? 'Live spot index' : 'No spot data'}>
                <span className="text-amber-400 font-semibold text-[8px]">S</span>
                <span className="text-[var(--text-secondary)]">
                  {spotPrice !== null && spotPrice > 0 ? spotPrice.toFixed(2) : '—'}
                </span>
              </div>
            </div>
            
            {/* Row 3: Visual God Score Bar (-50 to +50) */}
            {/* Color thresholds: Green > 20, Grey -20 to 20, Red < -20 */}
            <div className="mt-1.5 relative h-4 bg-[var(--bg-surface)] rounded overflow-hidden border border-[var(--border-color)]/30">
              {/* Center line */}
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[var(--text-muted)]/50 z-10" />
              {/* Score fill bar with smooth color transition */}
              <div 
                className={`absolute top-0 bottom-0 transition-all duration-500 ease-out ${
                  score > 20 ? 'bg-green-500/90 left-1/2' : 
                  score < -20 ? 'bg-red-500/90 right-1/2' : 
                  score > 0 ? 'bg-slate-400/60 left-1/2' :
                  score < 0 ? 'bg-slate-400/60 right-1/2' : 'bg-slate-500/30 left-1/2'
                }`}
                style={{
                  width: `${Math.min(Math.abs(score), 50) / 50 * 50}%`
                }}
              />
              {/* Score label overlay */}
              <span className={`absolute inset-0 flex items-center justify-center text-[9px] font-bold z-20 transition-colors duration-300 ${
                score > 20 ? 'text-white' :
                score < -20 ? 'text-white' :
                score > 0 ? 'text-green-400' :
                score < 0 ? 'text-red-400' : 'text-[var(--text-muted)]'
              }`}>
                {score > 0 ? '+' : ''}{score}
              </span>
            </div>
            
            {/* Row 4: Direction + Status */}
            <div className="flex items-center justify-between mt-1">
              <span className={`text-[10px] font-bold ${
                data.direction === 'UP' ? 'text-profit' : 
                data.direction === 'DOWN' ? 'text-loss' : 'text-muted'
              }`}>
                {data.direction === 'UP' ? '↑ BULL' : data.direction === 'DOWN' ? '↓ BEAR' : '— FLAT'}
              </span>
              {/* VISUAL FIX: Show "Last" with gray indicator when market is closed */}
              <span className={`text-[8px] ${isMarketOpen && data.running ? 'text-green-500' : isMarketOpen ? 'text-red-500' : 'text-slate-500'}`}>
                {isMarketOpen ? (data.running ? '● Live' : '○ Off') : '○ Last'}
              </span>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <aside 
      className={`
        sidebar relative flex flex-col
        ${isCollapsed ? 'collapsed' : 'expanded'}
        h-full shrink-0
      `}
      data-testid="pro-sidebar"
    >
      {/* Logo Header */}
      <div className="sidebar-header">
        <div className={`flex items-center gap-2 ${isCollapsed ? 'justify-center' : ''}`}>
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-purple)] flex items-center justify-center shrink-0">
            <span className="font-bold text-white text-sm">AG</span>
          </div>
          {!isCollapsed && (
            <div className="min-w-0">
              <div className="text-sm font-bold text-[var(--text-primary)] leading-none">AlgoGreeks</div>
              <div className="text-[10px] text-[var(--accent-primary)] uppercase tracking-wider">HFT Engine</div>
            </div>
          )}
        </div>
      </div>

      {/* Collapse Toggle */}
      <button
        onClick={toggleCollapse}
        className="absolute -right-3 top-12 w-6 h-6 bg-[var(--bg-surface)] border border-[var(--border-card)] rounded-full flex items-center justify-center hover:bg-[var(--bg-card-hover)] hover:border-[var(--accent-primary)] z-10 transition-all"
        data-testid="sidebar-toggle"
      >
        {isCollapsed ? <ChevronRight className="w-3 h-3 text-[var(--text-secondary)]" /> : <ChevronLeft className="w-3 h-3 text-[var(--text-secondary)]" />}
      </button>

      {/* Sidebar Content */}
      <div className="sidebar-content">
        {/* Dashboard Link */}
        <Link
          to="/"
          className={`sidebar-nav-item ${isActive('/') && location.pathname === '/' ? 'active' : ''}`}
          data-testid="nav-dashboard"
        >
          <LayoutDashboard className="icon" />
          {!isCollapsed && <span className="label">Dashboard</span>}
        </Link>

        {/* Watchlist Section */}
        <div className="mt-2">
          {!isCollapsed && (
            <div className="text-[9px] font-bold text-[var(--text-muted)] uppercase tracking-widest px-3 mb-1">
              WATCHLIST
            </div>
          )}
          {renderInstrumentCard('NIFTY', '/nifty')}
          {renderInstrumentCard('BANKNIFTY', '/banknifty')}
        </div>

        {/* SESSION 112: Analytics moved to Admin Panel */}

        {/* Spacer */}
        <div className="flex-1" />

        {/* SESSION 112: Admin link moved after VIX and Flow indicators */}

        {/* SESSION 92: Removed duplicate "Replay" link - Time Machine serves the same purpose */}
        
        {/* SESSION 112: SUPPORT & RESISTANCE - Compact display */}
        {!isCollapsed && (
          <div className="mx-2 mb-2" data-testid="sidebar-support-resistance">
            <div className="text-[9px] font-bold text-[var(--text-muted)] uppercase tracking-widest mb-1">
              S & R
            </div>
            {(() => {
              const gexLevels = store[activeSymbol]?.gexLevels || {};
              // SESSION 112 FIX: Use GEX API's own price (same API call as call_wall/put_wall)
              // This ensures price and S/R levels are from the same data snapshot
              // Fallback to store price only if GEX price not available
              const gexPrice = gexLevels?.futures_price || gexLevels?.spot_price;
              const storePrice = store[activeSymbol]?.futuresPrice || store[activeSymbol]?.spotPrice;
              const price = gexPrice || storePrice || 0;
              
              const resistance = gexLevels?.call_wall;
              const support = gexLevels?.put_wall;
              
              // SESSION 112 FIX: Validate price is in reasonable range for the symbol
              // NIFTY: 15000-35000, BANKNIFTY: 30000-80000
              const minPrice = activeSymbol === 'BANKNIFTY' ? 30000 : 15000;
              const maxPrice = activeSymbol === 'BANKNIFTY' ? 80000 : 35000;
              const isValidPrice = price >= minPrice && price <= maxPrice;
              
              // Only calculate distance if price is valid
              const resDistance = isValidPrice && resistance ? Math.abs(resistance - price) : 0;
              const supDistance = isValidPrice && support ? Math.abs(price - support) : 0;
              
              // Distance should be within 5% of price (reasonable S/R distance)
              const maxReasonableDistance = price * 0.05;
              const showResDistance = resDistance > 0 && resDistance < maxReasonableDistance;
              const showSupDistance = supDistance > 0 && supDistance < maxReasonableDistance;
              
              return (
                <div className="space-y-1">
                  {/* Resistance (Call Wall) - Compact */}
                  <div className="flex items-center justify-between px-2 py-1 rounded border bg-red-500/10 border-red-500/30">
                    <span className="text-red-400 text-[9px] font-bold">RES</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold font-mono text-red-400">
                        {resistance?.toLocaleString() || '--'}
                      </span>
                      {showResDistance && (
                        <span className="text-[8px] text-red-400/70 font-mono">
                          ↑{resDistance.toFixed(0)}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Support (Put Wall) - Compact */}
                  <div className="flex items-center justify-between px-2 py-1 rounded border bg-green-500/10 border-green-500/30">
                    <span className="text-green-400 text-[9px] font-bold">SUP</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold font-mono text-green-400">
                        {support?.toLocaleString() || '--'}
                      </span>
                      {showSupDistance && (
                        <span className="text-[8px] text-green-400/70 font-mono">
                          ↓{supDistance.toFixed(0)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
        {isCollapsed && (
          <div className="mx-1 mb-2 space-y-1" data-testid="sidebar-sr-collapsed">
            {(() => {
              const gexLevels = store[activeSymbol]?.gexLevels || {};
              const resistance = gexLevels?.call_wall;
              const support = gexLevels?.put_wall;
              
              return (
                <>
                  <div className="flex flex-col items-center px-1 py-0.5 rounded border bg-red-500/10 border-red-500/30" title={`Resistance: ${resistance?.toLocaleString() || '--'}`}>
                    <span className="text-[7px] font-bold text-red-400">R</span>
                    <span className="text-[8px] font-bold font-mono text-red-400">
                      {resistance ? (resistance/1000).toFixed(1) + 'K' : '--'}
                    </span>
                  </div>
                  <div className="flex flex-col items-center px-1 py-0.5 rounded border bg-green-500/10 border-green-500/30" title={`Support: ${support?.toLocaleString() || '--'}`}>
                    <span className="text-[7px] font-bold text-green-400">S</span>
                    <span className="text-[8px] font-bold font-mono text-green-400">
                      {support ? (support/1000).toFixed(1) + 'K' : '--'}
                    </span>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* SESSION 111: VIX Indicator moved from KPI bar to sidebar */}
        {/* SESSION 128: VIX now uses LKG pattern - never shows "--" */}
        {!isCollapsed && (
          <div className="mx-2 mb-2" data-testid="sidebar-vix-indicator">
            <div className="text-[9px] font-bold text-[var(--text-muted)] uppercase tracking-widest mb-1">
              VOLATILITY
            </div>
            {(() => {
              const godScore = store[activeSymbol]?.godScore || {};
              // SESSION 128: Use LKG pattern - update ref only when we have valid VIX
              const rawVix = godScore?.vix_value;
              if (rawVix && rawVix > 0) {
                lastKnownGoodVixRef.current = rawVix;
              }
              // Use LKG if available, otherwise don't render (no more 15.0 fallback)
              const vixValue = lastKnownGoodVixRef.current;
              
              // If no VIX has ever been received, don't show the component
              if (!vixValue || vixValue <= 0) {
                return null;
              }
              
              const getVixColor = () => {
                if (vixValue < 13) return 'text-green-400 bg-green-500/10 border-green-500/30';
                if (vixValue <= 18) return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30';
                return 'text-red-400 bg-red-500/10 border-red-500/30';
              };
              
              const getVixLabel = () => {
                if (vixValue < 13) return 'LOW';
                if (vixValue <= 18) return 'NORMAL';
                return 'HIGH';
              };
              
              return (
                <div className={`flex items-center justify-between px-2 py-1.5 rounded-md border ${getVixColor()}`}>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-semibold">VIX</span>
                    <span className="text-sm font-bold font-mono">
                      {vixValue?.toFixed(1)}
                    </span>
                  </div>
                  <span className="text-[8px] font-bold uppercase">{getVixLabel()}</span>
                </div>
              );
            })()}
          </div>
        )}
        {isCollapsed && (
          <div className="mx-1 mb-2" data-testid="sidebar-vix-collapsed">
            {(() => {
              const godScore = store[activeSymbol]?.godScore || {};
              // SESSION 128: Use LKG pattern - update ref only when we have valid VIX
              const rawVix = godScore?.vix_value;
              if (rawVix && rawVix > 0) {
                lastKnownGoodVixRef.current = rawVix;
              }
              // Use LKG if available
              const vixValue = lastKnownGoodVixRef.current;
              
              // If no VIX has ever been received, don't show
              if (!vixValue || vixValue <= 0) {
                return null;
              }
              
              const getVixColor = () => {
                if (vixValue < 13) return 'text-green-400 bg-green-500/10 border-green-500/30';
                if (vixValue <= 18) return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30';
                return 'text-red-400 bg-red-500/10 border-red-500/30';
              };
              
              return (
                <div className={`flex flex-col items-center px-1 py-1 rounded border ${getVixColor()}`} title={`India VIX: ${vixValue?.toFixed(2)}`}>
                  <span className="text-[8px] font-bold">VIX</span>
                  <span className="text-[10px] font-bold font-mono">
                    {vixValue?.toFixed(0)}
                  </span>
                </div>
              );
            })()}
          </div>
        )}
        
        {/* SESSION 112: Flow & Imbalance Indicator */}
        {!isCollapsed && (
          <div className="mx-2 mb-2" data-testid="sidebar-flow-imbalance">
            <div className="text-[9px] font-bold text-[var(--text-muted)] uppercase tracking-widest mb-1">
              FLOW & IMBALANCE
            </div>
            {(() => {
              const pulse = store[activeSymbol]?.strategyPulse || {};
              const session79 = pulse?.session_79 || {};
              const flowScore = session79?.order_flow_score || 0;
              const profileScore = session79?.liquidity_score || 0;
              const imbalance = session79?.imbalance_state || {};
              const imbalanceDir = imbalance?.direction || 'NEUTRAL';
              const buyRatio = imbalance?.buy_ratio || 50;
              const sellRatio = imbalance?.sell_ratio || 50;
              
              const getImbalanceStyle = () => {
                if (imbalanceDir === 'BUY') return 'text-green-400 bg-green-500/10 border-green-500/30';
                if (imbalanceDir === 'SELL') return 'text-red-400 bg-red-500/10 border-red-500/30';
                return 'text-gray-400 bg-gray-500/10 border-gray-500/30';
              };
              
              return (
                <div className="space-y-1.5">
                  {/* Flow & Profile Scores */}
                  <div className="flex items-center gap-2 text-[10px] font-mono">
                    <div className="flex-1 flex items-center justify-between px-2 py-1 rounded bg-cyan-500/10 border border-cyan-500/20">
                      <span className="text-cyan-400 text-[9px]">Flow</span>
                      <span className={`font-bold ${flowScore > 0 ? 'text-green-400' : flowScore < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                        {flowScore > 0 ? '+' : ''}{flowScore.toFixed(1)}
                      </span>
                    </div>
                    <div className="flex-1 flex items-center justify-between px-2 py-1 rounded bg-purple-500/10 border border-purple-500/20">
                      <span className="text-purple-400 text-[9px]">Profile</span>
                      <span className={`font-bold ${profileScore > 0 ? 'text-green-400' : profileScore < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                        {profileScore > 0 ? '+' : ''}{profileScore.toFixed(1)}
                      </span>
                    </div>
                  </div>
                  {/* Imbalance State */}
                  <div className={`flex items-center justify-between px-2 py-1.5 rounded-md border ${getImbalanceStyle()}`}>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-semibold">
                        {imbalanceDir === 'BUY' ? '🟢' : imbalanceDir === 'SELL' ? '🔴' : '⚪'} {imbalanceDir}
                      </span>
                    </div>
                    <span className="text-[9px] font-mono">
                      B:{buyRatio.toFixed(0)} | S:{sellRatio.toFixed(0)}
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
        {isCollapsed && (
          <div className="mx-1 mb-2" data-testid="sidebar-flow-collapsed">
            {(() => {
              const pulse = store[activeSymbol]?.strategyPulse || {};
              const session79 = pulse?.session_79 || {};
              const flowScore = session79?.order_flow_score || 0;
              const imbalance = session79?.imbalance_state || {};
              const imbalanceDir = imbalance?.direction || 'NEUTRAL';
              
              const getColor = () => {
                if (imbalanceDir === 'BUY') return 'text-green-400 bg-green-500/10 border-green-500/30';
                if (imbalanceDir === 'SELL') return 'text-red-400 bg-red-500/10 border-red-500/30';
                return 'text-gray-400 bg-gray-500/10 border-gray-500/30';
              };
              
              return (
                <div className={`flex flex-col items-center px-1 py-1 rounded border ${getColor()}`} title={`Flow: ${flowScore.toFixed(1)} | ${imbalanceDir}`}>
                  <span className="text-[8px] font-bold">FLOW</span>
                  <span className="text-[10px] font-bold font-mono">
                    {flowScore > 0 ? '+' : ''}{flowScore.toFixed(0)}
                  </span>
                </div>
              );
            })()}
          </div>
        )}
        
        {/* SESSION 112: GEX ANCHOR moved to Top Bar GEX section */}
        
        {/* SESSION 112: Analytics, Time Machine, TUNE moved to Admin Panel */}
        {/* These are now accessible via Admin → Tools tab */}
        
        {/* Admin Link - Visible only to admins */}
        {isAdmin && (
          <Link
            to="/admin"
            className={`sidebar-nav-item ${isActive('/admin') ? 'active' : ''}`}
            data-testid="nav-admin"
          >
            <Settings className="icon" />
            {!isCollapsed && <span className="label">Admin</span>}
          </Link>
        )}

        {/* SESSION 109: Subscription Countdown for non-admin users */}
        {!isAdmin && user?.subscription_end_date && (
          <SubscriptionCountdown 
            endDate={user.subscription_end_date} 
            isCollapsed={isCollapsed}
            onExpired={logout}
          />
        )}
      </div>

      {/* SESSION 131: Footer section - pinned at bottom, always visible */}
      <div className="sidebar-footer flex flex-col gap-1 p-2 border-t border-[var(--border-subtle)] mt-auto shrink-0">
        {/* SESSION 122: FAQ Button */}
        <button
          onClick={() => setShowFAQ(true)}
          className="sidebar-nav-item text-cyan-400 hover:bg-cyan-500/10"
          data-testid="nav-faq"
          title="Frequently Asked Questions"
        >
          <HelpCircle className="icon" />
          {!isCollapsed && <span className="label">FAQ</span>}
        </button>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className="sidebar-nav-item text-[var(--color-loss)] hover:bg-[var(--color-sell-dim)]"
          data-testid="nav-logout"
        >
          <LogOut className="icon" />
          {!isCollapsed && <span className="label">Logout</span>}
        </button>
      </div>
      
      {/* SESSION 122: FAQ Modal */}
      <FAQModal isOpen={showFAQ} onClose={() => setShowFAQ(false)} />
    </aside>
  );
};

export default Sidebar;
